<template>
  <!-- API 異常時整個區塊不顯示，不影響既有績效內容（CNCY-10775）；還沒有建議時顯示說明（CNCY-10866） -->
  <div v-if="loaded" class="chart-container">
    <div class="chart-header">
      <div class="flex items-center" style="gap: 8px">
        <MaterialIcon :size="20" class="text-primary-100">auto_awesome</MaterialIcon>
        <p>個人績效與業績優化建議</p>
        <TipInfo :size="16">{{ tooltipText }}</TipInfo>
      </div>
      <p v-if="advice && !advice.isBenchmarkLatest" class="text-sub text-gray-60">
        資料期間：{{ benchmarkRange }}
      </p>
    </div>
    <div class="divider" />

    <EmptyBlock v-if="!advice" class="advice-empty" :emptyText="emptyText" />

    <div v-else class="advice-content">
      <section class="advice-section">
        <p class="section-title">AI 績效摘要</p>
        <p class="summary">{{ advice.summary }}</p>
        <ul v-if="notes.length" class="notes">
          <li v-for="note in notes" :key="note">{{ note }}</li>
        </ul>
      </section>

      <div class="metric-sections">
        <section v-for="section in metricSections" :key="section.key" class="advice-section metric-section">
          <p class="section-title">{{ section.title }}</p>
          <AdviceMetricItem
            v-for="item in section.items"
            :key="item.metricKey"
            :item="item"
            :tagType="section.tagType"
          />
          <p v-if="!section.items.length" class="empty-text">{{ section.emptyText }}</p>
        </section>
      </div>

      <section class="advice-section">
        <p class="section-title">AI 業績優化建議</p>
        <div v-for="(item, index) in advice.suggestions" :key="index" class="suggestion-item">
          <div class="flex items-center" style="gap: 8px">
            <span class="suggestion-index">{{ index + 1 }}</span>
            <p class="suggestion-title">{{ item.title }}</p>
            <Tag v-if="item.metricName" type="info" size="small">{{ item.metricName }}</Tag>
          </div>
          <div class="suggestion-row">
            <p class="suggestion-label">建議理由</p>
            <p>{{ item.reason }}</p>
          </div>
          <div class="suggestion-row">
            <p class="suggestion-label">可行方向</p>
            <p>{{ item.action }}</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeMount, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { get } from 'lodash'
import TipInfo from '@/components/TipInfo.vue'
import MaterialIcon from '@/components/MaterialIcon.vue'
import Tag from '@/components/Tag/Tag.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import AdviceMetricItem from './AdviceMetricItem.vue'
import { GetBeautyAIAptUnitAnalysisAdvice } from '@/api/beautyAI'
import { useShop } from '@/use/shop'

// YYYY-MM → YYYY/MM
const formatMonth = (month) => (month ? month.replace('-', '/') : '')

// 建議每月依上個月以前完成的預約產生；新服務人員、上個月以前沒有完成的預約時 API 回傳 null
const emptyText = '尚未產生建議。系統於每月初依上個月以前已完成的預約產生建議，上個月以前沒有完成的預約時不會產生'

export default defineComponent({
  name: 'BeautyAIAppointmentUnitAdvice',
  components: { TipInfo, MaterialIcon, Tag, EmptyBlock, AdviceMetricItem },
  setup () {
    const route = useRoute()
    const { shopId } = useShop()
    const loaded = ref(false)
    const advice = ref(null)

    const benchmarkRange = computed(() => {
      return `${formatMonth(get(advice.value, 'benchmarkFromMonth'))}～${formatMonth(get(advice.value, 'benchmarkPeriod'))}`
    })

    // 設定為全平台時，PR 是和全平台的服務人員比較（peerScope 為 all）
    const isPeerAll = computed(() => get(advice.value, 'peerScope') === 'all')
    const peerLabel = computed(() => (isPeerAll.value ? '全平台' : '同業'))
    const tooltipText = computed(() => (isPeerAll.value
      ? '此建議是以個別服務人員的數值與系統全部組織人員數值比較，代表該人員在全平台的表現'
      : '此建議是以個別服務人員的數值與系統全部同產業組織人員數值比較，代表該人員在該產業的表現'))

    // 資料不足、近期沒有預約、單人店、分店沒有設定產業、同業只有本人時，對應的比較不會出現，說明原因
    const notes = computed(() => {
      if (!advice.value) return []
      const { trendType, hasTrend, historyMonths, hasShopComparison, hasPeerComparison, isShopIndustryMissing } = advice.value
      const list = []
      if (!trendType) {
        list.push(`目前只有 ${historyMonths} 個月的資料，暫不判斷個人趨勢`)
      } else if (!hasTrend) {
        list.push('近期沒有完成的預約，暫不判斷個人趨勢')
      } else if (trendType === 'short') {
        list.push(`資料未滿 12 個月，個人趨勢以現有 ${historyMonths} 個月計算，僅供參考`)
      }
      if (!hasShopComparison) list.push('店內沒有其他服務人員的資料，不做店內比較')
      if (isShopIndustryMissing) list.push('分店尚未設定產業類別，不做同業比較（可於「服務人員設定」設定）')
      else if (!hasPeerComparison) list.push(`${peerLabel.value}沒有其他服務人員的資料，不做${peerLabel.value}比較`)
      return list
    })

    const metricSections = computed(() => [
      {
        key: 'strengths',
        title: '我的優勢',
        items: get(advice.value, 'strengths', []),
        emptyText: '目前沒有明顯優於基準的指標',
        tagType: 'success',
      },
      {
        key: 'concerns',
        title: '值得關注',
        items: get(advice.value, 'concerns', []),
        emptyText: '沒有明顯待加強的指標，維持目前表現',
        tagType: 'warning',
      },
    ])

    onBeforeMount(async () => {
      if (!shopId.value) return
      const [res, err] = await GetBeautyAIAptUnitAnalysisAdvice({
        shopId: shopId.value,
        id: route.params.id,
      })
      if (err) return
      advice.value = get(res, 'advice') || null
      loaded.value = true
    })

    return {
      loaded,
      advice,
      emptyText,
      benchmarkRange,
      tooltipText,
      notes,
      metricSections,
    }
  },
})
</script>

<style lang="postcss" scoped>
.chart-container {
  @apply bg-white rounded-[8px] p-[20px];
}
.chart-header {
  @apply flex items-center justify-between text-gray-80;
}
.divider {
  @apply w-full h-[2px] bg-[#ECECEC] mt-[16px] mb-[20px];
}
.advice-content {
  @apply flex flex-col gap-[24px];
}
.advice-empty {
  @apply text-gray-60;
}
.advice-section {
  @apply flex flex-col gap-[12px];
}
.section-title {
  @apply font-medium text-gray-100;
}
.summary {
  @apply bg-primary-30 rounded-[8px] p-[16px] text-gray-100 leading-[24px];
}
.notes {
  @apply list-disc pl-[20px] text-sub text-gray-60;
}
.metric-sections {
  @apply flex flex-wrap gap-[20px];
}
.metric-section {
  flex: 1 1 320px;
}
.empty-text {
  @apply text-sub text-gray-60;
}
.suggestion-item {
  @apply flex flex-col gap-[8px] border border-gray-40 rounded-[8px] p-[16px];
}
.suggestion-index {
  @apply flex items-center justify-center flex-shrink-0;
  @apply w-[24px] h-[24px] rounded-full bg-primary-100 text-white text-sub;
}
.suggestion-title {
  @apply font-medium text-gray-100;
}
.suggestion-row {
  @apply flex gap-[8px] text-gray-80 leading-[22px];
}
.suggestion-label {
  @apply text-gray-60 flex-shrink-0;
}
</style>
